import { useCallback, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Page, { type PageProps } from "@/v2-backoffice-client/components/page"
import LegalBillingBoard from "@/v2-backoffice-client/legalBilling/LegalBillingBoard"
import { useLegalBillingQuery } from "@/v2-backoffice-client/legalBilling/legalBilling.query"
import { useLegalBillingSetInvoicedMutation } from "@/v2-backoffice-client/legalBilling/legalBilling.setInvoiced.mutation"
import {
  billingRangeToQueryVariables,
  defaultBillingRange,
  isValidBillingRange,
} from "@/v2-backoffice-client/legalBilling/legalBilling.utils"
import type { LegalBillingInvoiceAction } from "@/v2-backoffice-client/legalBilling/legalBilling.types"

export type LegalBillingPageProps = PageProps

function skippedNoticeText(count: number): string {
  const rows = count === 1 ? "row was" : "rows were"
  return `${count} ${rows} left unmarked: the document is still pending or has failed, so there is nothing to bill yet. Everything else in the group was marked.`
}

const RANGE_INPUTS = [
  { id: "legal-billing-from", label: "From", key: "from" as const },
  { id: "legal-billing-to", label: "To", key: "to" as const },
]

// The cross-tenant land-register document billing dashboard (T36): every tenant's document
// spend in one place, plus marking batches invoiced once they have been billed.
//
// Owns the three pieces of state the presentational tree below it takes as props: the date
// range, the hide-invoiced toggle, and the query/mutation. Everything under
// LegalBillingBoard is pure props, so the grouping and totals render can be tested without
// an Apollo provider.
function LegalBillingPage(props: LegalBillingPageProps) {
  const { className, ...pageProps } = props

  const [range, setRange] = useState(defaultBillingRange)
  const [isHidingInvoiced, setIsHidingInvoiced] = useState(false)

  const isValidRange = isValidBillingRange(range)

  // An all-tenants read is the one query here whose cost scales with the window, so it
  // never fires on a half-typed range.
  const { data, loading, error } = useLegalBillingQuery({
    variables: billingRangeToQueryVariables(range),
    skip: !isValidRange,
    fetchPolicy: "cache-and-network",
  })

  // The proc refuses to mark rows whose document is still pending or has failed, and
  // reports them rather than failing the call. Nothing else surfaces that, and a silent
  // partial mark on the money path is how a group gets billed as done while some of it
  // never was — so it is held here and shown until the next action.
  const [skippedNotice, setSkippedNotice] = useState<string | null>(null)

  const [setInvoiced, { loading: isSettingInvoiced }] =
    useLegalBillingSetInvoicedMutation({
      onCompleted: (result) => {
        const skipped =
          result.setLegalDocumentPurchasesInvoiced.SkippedUnsettledCount
        setSkippedNotice(skipped > 0 ? skippedNoticeText(skipped) : null)
      },
    })

  const handleInvoice = useCallback(
    (action: LegalBillingInvoiceAction) => {
      if (action.purchaseIds.length === 0) return
      setSkippedNotice(null)
      void setInvoiced({
        variables: {
          purchaseIds: action.purchaseIds,
          isInvoiced: action.isInvoiced,
        },
      })
    },
    [setInvoiced],
  )

  const summary = data?.getLegalDocumentPurchasesAllTenants

  return (
    <Page className={cn("legal-billing-page", className)} {...pageProps}>
      <Page.Header>
        <Page.Title>Legal billing</Page.Title>
        <Page.Header.Actions>
          {RANGE_INPUTS.map((input) => (
            <div key={input.id} className="flex items-center gap-2">
              <Label htmlFor={input.id} className="text-sm">
                {input.label}
              </Label>
              <Input
                id={input.id}
                type="date"
                value={range[input.key]}
                data-feature-code={`UI_BackofficeLegalBilling_Range_${input.label}`}
                onChange={(event) =>
                  setRange((current) => ({
                    ...current,
                    [input.key]: event.target.value,
                  }))
                }
              />
            </div>
          ))}
        </Page.Header.Actions>
      </Page.Header>

      <Page.Content>
        {!isValidRange ? (
          <p className="text-sm text-muted-foreground">
            Pick a date range (From must not be after To).
          </p>
        ) : error ? (
          <p className="text-sm text-destructive">{error.message}</p>
        ) : !summary ? (
          <p className="text-sm text-muted-foreground">
            {loading ? "Loading purchases…" : "No data."}
          </p>
        ) : (
          <>
            {skippedNotice ? (
              <p
                role="status"
                className="mb-3 rounded-sm border border-warning bg-warning/10 px-3 py-2 text-sm"
              >
                {skippedNotice}
              </p>
            ) : null}
            <LegalBillingBoard
              summary={summary}
              isHidingInvoiced={isHidingInvoiced}
              onHidingInvoicedChange={setIsHidingInvoiced}
              isPending={isSettingInvoiced}
              onInvoice={handleInvoice}
            />
          </>
        )}
      </Page.Content>
    </Page>
  )
}

export default LegalBillingPage
